import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Checkbox } from "../Checkbox";
import { cx } from "../cx";
import { Icon } from "../Icon";
import { type Placement, Popover, useDisclosure } from "../Popover";
import styles from "./Combobox.module.css";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Options sharing a group render under one 12px uppercase header, in first-seen order. */
  group?: string;
  /** Project colour: renders a dot and tints the label, like the tracker project picker. */
  color?: string;
  /** Muted suffix after the label (the ` - <client>` part of picker rows). */
  hint?: string;
  disabled?: boolean;
}

interface CommonProps {
  options: ComboboxOption[];
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Shows the footer create row; receives the current search text. */
  onCreate?: (text: string) => void;
  createLabel?: string;
  /** Extra footer content under the create row. */
  footer?: ReactNode;
  selectAllLabel?: string;
  /** Panel width, px. The tracker project picker is 360. */
  width?: number;
  placement?: Placement;
  disabled?: boolean;
  /** Replaces the trigger's content (the outline button keeps its frame and caret). */
  renderValue?: (selected: ComboboxOption[]) => ReactNode;
  renderOption?: (option: ComboboxOption, selected: boolean) => ReactNode;
  triggerClassName?: string;
  className?: string;
  "aria-label"?: string;
  id?: string;
}

export interface SingleComboboxProps extends CommonProps {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
}

export interface MultiComboboxProps extends CommonProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
  /** Adds a "Select all" row that toggles every visible option. */
  showSelectAll?: boolean;
}

export type ComboboxProps = SingleComboboxProps | MultiComboboxProps;

const SELECT_ALL = "__select_all__";

export function filterOptions(options: ComboboxOption[], query: string): ComboboxOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.label.toLowerCase().includes(q) || o.hint?.toLowerCase().includes(q));
}

function groupOptions(options: ComboboxOption[]): Array<{ group: string | undefined; items: ComboboxOption[] }> {
  const out: Array<{ group: string | undefined; items: ComboboxOption[] }> = [];
  for (const o of options) {
    const bucket = out.find((g) => g.group === o.group);
    if (bucket) bucket.items.push(o);
    else out.push({ group: o.group, items: [o] });
  }
  return out;
}

export function Combobox(props: ComboboxProps) {
  const {
    options,
    placeholder = "Select",
    searchPlaceholder = "Search",
    emptyText = "No matching items",
    onCreate,
    createLabel = "Create new",
    footer,
    selectAllLabel = "Select all",
    width = 360,
    placement = "bottom-start",
    disabled,
    renderValue,
    renderOption,
    triggerClassName,
    className,
    id,
  } = props;
  const multiple = props.multiple === true;
  const selectedValues = useMemo(
    () => (props.multiple ? props.value : props.value === null ? [] : [props.value]),
    [props.multiple, props.value],
  );
  const showSelectAll = props.multiple === true && props.showSelectAll === true;

  const { open, setOpen, close, anchorRef } = useDisclosure<HTMLButtonElement>();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const baseId = useId();
  const listId = `${baseId}-list`;
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => filterOptions(options, query), [options, query]);
  const groups = useMemo(() => groupOptions(filtered), [filtered]);
  // Keyboard order follows render order (groups flattened), so ArrowDown never lands on a header.
  const navigable = useMemo(() => {
    const flat = groups.flatMap((g) => g.items).filter((o) => !o.disabled);
    return showSelectAll && flat.length > 0 ? [SELECT_ALL, ...flat.map((o) => o.value)] : flat.map((o) => o.value);
  }, [groups, showSelectAll]);

  const isSelected = useCallback((v: string) => selectedValues.includes(v), [selectedValues]);
  const visibleEnabled = useMemo(() => filtered.filter((o) => !o.disabled).map((o) => o.value), [filtered]);
  const allVisibleSelected = visibleEnabled.length > 0 && visibleEnabled.every(isSelected);
  const someVisibleSelected = visibleEnabled.some(isSelected);

  // Runs only on open: re-running when the selection changes would yank the cursor in multi mode.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on `open` only
  useEffect(() => {
    if (!open) return;
    setQuery("");
    const firstSelected = navigable.findIndex((v) => v !== SELECT_ALL && selectedValues.includes(v));
    setActive(Math.max(firstSelected, 0));
  }, [open]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, navigable.length - 1)));
  }, [navigable.length]);

  const activeValue = navigable[active];
  const optionId = (v: string) => `${baseId}-opt-${v === SELECT_ALL ? "all" : encodeURIComponent(v)}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: optionId only depends on the stable baseId
  useEffect(() => {
    if (!open || activeValue === undefined) return;
    const el = document.getElementById(optionId(activeValue));
    el?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeValue]);

  const choose = (value: string) => {
    if (props.multiple) {
      if (value === SELECT_ALL) {
        const next = allVisibleSelected
          ? props.value.filter((v) => !visibleEnabled.includes(v))
          : Array.from(new Set([...props.value, ...visibleEnabled]));
        props.onChange(next);
        return;
      }
      props.onChange(props.value.includes(value) ? props.value.filter((v) => v !== value) : [...props.value, value]);
      return;
    }
    props.onChange(value);
    close();
    anchorRef.current?.focus();
  };

  const create = () => {
    if (!onCreate) return;
    onCreate(query.trim());
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const n = navigable.length;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (n) setActive((a) => (a + 1) % n);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (n) setActive((a) => (a - 1 + n) % n);
        break;
      case "Home":
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        e.preventDefault();
        setActive(Math.max(0, n - 1));
        break;
      case "Enter":
        e.preventDefault();
        // Clockify's pickers create the typed entity with Ctrl+Enter.
        if ((e.ctrlKey || e.metaKey) && onCreate) create();
        else if (activeValue !== undefined) choose(activeValue);
        else if (onCreate && query.trim()) create();
        break;
      case "Tab":
        close();
        break;
    }
  };

  const selectedOptions = options.filter((o) => isSelected(o.value));
  let triggerContent: ReactNode;
  if (renderValue) triggerContent = renderValue(selectedOptions);
  else if (selectedOptions.length === 0) triggerContent = <span className={styles.placeholder}>{placeholder}</span>;
  else if (multiple) triggerContent = selectedOptions.map((o) => o.label).join(", ");
  else triggerContent = <OptionLabel option={selectedOptions[0] as ComboboxOption} />;

  return (
    <>
      <button
        ref={anchorRef}
        id={id}
        type="button"
        className={cx(styles.trigger, triggerClassName)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={props["aria-label"]}
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        <span className={styles.triggerValue}>{triggerContent}</span>
        <Icon name="chevron-down" size={16} className={styles.caret} />
      </button>
      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        placement={placement}
        surface="card"
        className={cx(styles.panel, className)}
        style={{ width } as CSSProperties}
      >
        <div className={styles.search}>
          <Icon name="search" size={16} className={styles.searchIcon} />
          <input
            data-autofocus
            className={styles.searchInput}
            value={query}
            placeholder={searchPlaceholder}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeValue !== undefined ? optionId(activeValue) : undefined}
            aria-label={searchPlaceholder}
          />
        </div>
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-multiselectable={multiple || undefined}
          className={styles.list}
        >
          {filtered.length === 0 && <div className={styles.empty}>{emptyText}</div>}
          {showSelectAll && filtered.length > 0 && (
            // biome-ignore lint/a11y/useFocusableInteractive: focus stays on the search input (aria-activedescendant)
            // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled on the search input
            <div
              id={optionId(SELECT_ALL)}
              role="option"
              aria-selected={allVisibleSelected}
              className={cx(styles.option, activeValue === SELECT_ALL && styles.active)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(SELECT_ALL)}
            >
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                readOnly
                tabIndex={-1}
                className={styles.optionCheckbox}
              />
              {selectAllLabel}
            </div>
          )}
          {groups.map((g) => (
            // biome-ignore lint/a11y/useSemanticElements: ARIA group inside a listbox has no native equivalent
            <div key={g.group ?? "__none__"} role="group" aria-label={g.group} className={styles.section}>
              {g.group && (
                <div className={styles.groupHeader} aria-hidden="true">
                  {g.group}
                </div>
              )}
              {g.items.map((o) => {
                const sel = isSelected(o.value);
                return (
                  // biome-ignore lint/a11y/useFocusableInteractive: focus stays on the search input (aria-activedescendant)
                  // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled on the search input
                  <div
                    key={o.value}
                    id={optionId(o.value)}
                    role="option"
                    aria-selected={sel}
                    aria-disabled={o.disabled || undefined}
                    className={cx(
                      styles.option,
                      activeValue === o.value && styles.active,
                      sel && !multiple && styles.selected,
                      o.disabled && styles.disabled,
                    )}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => {
                      const i = navigable.indexOf(o.value);
                      if (i >= 0) setActive(i);
                    }}
                    onClick={() => !o.disabled && choose(o.value)}
                  >
                    {multiple && <Checkbox checked={sel} readOnly tabIndex={-1} className={styles.optionCheckbox} />}
                    {renderOption ? renderOption(o, sel) : <OptionLabel option={o} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {(onCreate || footer) && (
          <div className={styles.footer}>
            {onCreate && (
              <button type="button" className={styles.create} onClick={create}>
                <Icon name="plus-circle" size={16} />
                {createLabel}
                {query.trim() && <span className={styles.createQuery}>&nbsp;"{query.trim()}"</span>}
              </button>
            )}
            {footer}
          </div>
        )}
      </Popover>
    </>
  );
}

function OptionLabel({ option }: { option: ComboboxOption }) {
  return (
    <span className={styles.label}>
      {option.color && <span className={styles.dot} style={{ background: option.color }} aria-hidden="true" />}
      <span className={styles.labelText} style={option.color ? { color: option.color } : undefined}>
        {option.label}
      </span>
      {option.hint && <span className={styles.hint}>&nbsp;- {option.hint}</span>}
    </span>
  );
}
